import { JSX, useState, useEffect } from 'react';
import _ from 'lodash';
import { SelectSetting } from './SelectSetting';
import { ToggleSetting } from './ToggleSetting';
import { ColorSetting } from './ColorSetting';
import { SidebarWidget } from './SidebarWidget';
import { ColumnSettingsModal } from './ColumnSettingsModal';
import {
  PADDING_SIZES,
  PADDING_SIZES_WITH_NONE,
  TABLE_ITEMS_PER_PAGE,
  GRID_ITEMS_PER_PAGE,
  GRID_ITEM_IMAGE_MODES,
  FONTS,
  FONT_SIZES,
  TEXT_ALIGNMENTS,
  DATE_FORMATS,
  TIME_FORMATS,
} from '../constants';
import { Settings as SettingsType, ViewerBoardInfo } from '@gorilla/widgets-viewer/app/viewer/types';
import { IMAGE_FORMATS, DEFAULT_IMAGE_FORMAT_ID } from '@gorilla/widgets-shared/src/constants';
import { Table, Workspace, Status, Hide, Show, Settings, Layout, Column, Image, Gallery } from '@vibe/icons';
import { Icon, Tooltip, Flex, IconButton, TextField, Button } from '@vibe/core';
import { useDebounce } from '@uidotdev/usehooks';

type SettingProps = {
  settings: SettingsType;
  setSetting: <K extends keyof SettingsType>(key: K, value: SettingsType[K]) => void;
  boardInfo: ViewerBoardInfo;
  setHelpState: any;
  settingCategory: string;
  setShowHelpModal: any;
};

function Headline({ settings, setSetting }: SettingProps) {
  const [draftText, setDraftText] = useState(() => settings.headline || '');
  const draftTextDeferred = useDebounce(draftText, 200);

  useEffect(() => {
    if (draftTextDeferred !== settings.headline) {
      setSetting('headline', draftTextDeferred);
    }
  }, [draftTextDeferred]);

  return (
    <Flex direction="column" align="start" style={{ width: '100%' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <small>Widget headline</small>
      </label>
      <TextField
        size="medium"
        value={draftText}
        onChange={(newVal) => {
          setDraftText(newVal);
        }}
      />
    </Flex>
  );
}

function BodyPadding({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Widget margin"
      options={PADDING_SIZES_WITH_NONE}
      value={settings.bodyPadding}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('bodyPadding', newValue as SettingsType['bodyPadding']);
      }}
    />
  );
}

function PrimaryColor({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <ColorSetting
      title="Primary color"
      value={settings.primaryColor}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('primaryColor', newValue);
      }}
    />
  );
}

function SecondaryColor({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <ColorSetting
      title="Secondary color"
      value={settings.secondaryColor}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('secondaryColor', newValue);
      }}
    />
  );
}

function BorderColor({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <ColorSetting
      title="Border color"
      value={settings.borderColor}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('borderColor', newValue);
      }}
    />
  );
}

function BackgroundColor({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <ColorSetting
      title="Background color"
      value={settings.backgroundColor}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('backgroundColor', newValue);
      }}
    />
  );
}

function FontFamily({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Font family"
      options={FONTS}
      value={settings.fontFamily}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('fontFamily', newValue as SettingsType['fontFamily']);
      }}
    />
  );
}

function FontSize({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Font size"
      options={FONT_SIZES}
      value={settings.fontSize}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('fontSize', newValue as SettingsType['fontSize']);
      }}
    />
  );
}

function TextAlignment({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Text alignment"
      options={TEXT_ALIGNMENTS}
      value={settings.textAlignment}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('textAlignment', newValue as SettingsType['textAlignment']);
      }}
    />
  );
}

function SortColumn({ settings, setSetting, boardInfo, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = boardInfo.fields.map((field) => ({ label: field.label, value: field.id, leftIcon: Column }));

  return (
    <SelectSetting
      title="Sort column"
      options={options}
      value={settings.sortColumn}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('sortColumn', newValue as SettingsType['sortColumn']);
      }}
    />
  );
}

function SortDirection({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = [
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' },
  ];

  return (
    <SelectSetting
      title="Sort Direction"
      options={options}
      value={settings.sortDirection}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('sortDirection', newValue as SettingsType['sortDirection']);
      }}
    />
  );
}

function Search({ settings, setSetting }: SettingProps) {
  return (
    <ToggleSetting
      title="Show search"
      description="Disable search"
      value={settings.search}
      onChange={(newValue) => {
        setSetting('search', newValue);
      }}
    />
  );
}

function TableShowHeader({ settings, setSetting }: SettingProps) {
  return (
    <ToggleSetting
      title="Show table header"
      description="Show table header"
      value={settings.tableShowHeader}
      onChange={(newValue) => {
        setSetting('tableShowHeader', newValue);
      }}
    />
  );
}

function TableDefaultItemsPerPage({ settings, setSetting, setHelpState, settingCategory }: SettingProps) {
  return (
    <SelectSetting
      title="Default items per page"
      options={TABLE_ITEMS_PER_PAGE}
      value={settings.tableDefaultItemsPerPage}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      onChange={(newValue) => {
        setSetting('tableDefaultItemsPerPage', newValue as SettingsType['tableDefaultItemsPerPage']);
      }}
    />
  );
}

function TableCellPadding({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Cell margin"
      options={PADDING_SIZES}
      value={settings.tableCellPadding}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('tableCellPadding', newValue as SettingsType['tableCellPadding']);
      }}
    />
  );
}

function TableBorderRadius({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Table radius"
      options={PADDING_SIZES_WITH_NONE}
      value={settings.tableBorderRadius}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('tableBorderRadius', newValue as SettingsType['tableBorderRadius']);
      }}
    />
  );
}

function GridMode({ settings, setSetting }: SettingProps) {
  return (
    <ToggleSetting
      title="Enable item auto height"
      description="Items have dynamic height (masonry mode)"
      value={settings.gridMode === 'masonry'}
      onChange={(newValue) => {
        setSetting('gridMode', newValue ? 'masonry' : 'grid');
      }}
    />
  );
}

function GridItemsPerPage({ settings, setSetting, setHelpState, settingCategory }: SettingProps) {
  return (
    <SelectSetting
      title="Items per load"
      options={GRID_ITEMS_PER_PAGE}
      value={settings.gridItemsPerPage || 20}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      onChange={(newValue) => {
        setSetting('gridItemsPerPage', newValue as SettingsType['gridItemsPerPage']);
      }}
    />
  );
}

function GridItemPadding({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Item margin"
      options={PADDING_SIZES_WITH_NONE}
      value={settings.gridItemPadding}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('gridItemPadding', newValue as SettingsType['gridItemPadding']);
      }}
    />
  );
}

function GridItemGap({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Item gap"
      options={PADDING_SIZES}
      value={settings.gridItemGap}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('gridItemGap', newValue as SettingsType['gridItemGap']);
      }}
    />
  );
}

function GridItemBorderRadius({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Item radius"
      options={PADDING_SIZES_WITH_NONE}
      value={settings.gridItemBorderRadius}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('gridItemBorderRadius', newValue as SettingsType['gridItemBorderRadius']);
      }}
    />
  );
}

function GridItemImageColumn({ settings, setSetting, boardInfo, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = boardInfo.fields
    .filter((field) => field.type === 'file')
    .map((field) => ({ label: field.label, value: field.id, leftIcon: Column }));

  options.unshift({ label: 'None', value: '__none__', leftIcon: Hide });

  return (
    <SelectSetting
      title="Item image column"
      options={options}
      value={!settings.gridItemImageColumn ? '__none__' : settings.gridItemImageColumn}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        if (newValue === '__none__') {
          setSetting('gridItemImageColumn', null);
        } else {
          setSetting('gridItemImageColumn', newValue as SettingsType['gridItemImageColumn']);
        }
      }}
    />
  );
}

function GridItemImageColumnFormat({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = IMAGE_FORMATS.filter(({ active }) => active).map((format) => ({
    label: format.label,
    value: format.id,
    leftIcon: Layout,
  }));
  const value = settings.gridItemImageColumnFormat || DEFAULT_IMAGE_FORMAT_ID;

  return (
    <SelectSetting
      title="Item image format"
      options={options}
      value={value}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('gridItemImageColumnFormat', newValue as SettingsType['gridItemImageColumnFormat']);
      }}
    />
  );
}

function GridItemImageMode({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = GRID_ITEM_IMAGE_MODES.map((mode) => ({
    label: mode.label,
    value: mode.value,
    leftIcon: mode.value === 'thumbnail' ? Image : Gallery,
  }));

  return (
    <SelectSetting
      title="Item image mode"
      options={options}
      value={settings.gridItemImageMode || 'thumbnail'}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('gridItemImageMode', newValue as SettingsType['gridItemImageMode']);
      }}
    />
  );
}

function GridItemHeadlineColumn({ settings, setSetting, boardInfo, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = boardInfo.fields
    .filter((field) => field.type === 'text' || field.type === 'name')
    .map((field) => ({ label: field.label, value: field.id, leftIcon: Column }));

  options.unshift({ label: 'None', value: '__none__', leftIcon: Hide });

  return (
    <SelectSetting
      title="Item headline column"
      options={options}
      value={!settings.gridItemHeadlineColumn ? '__none__' : settings.gridItemHeadlineColumn}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        if (newValue === '__none__') {
          setSetting('gridItemHeadlineColumn', null);
        } else {
          setSetting('gridItemHeadlineColumn', newValue as SettingsType['gridItemHeadlineColumn']);
        }
      }}
    />
  );
}

function EventsItemPadding({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Item margin"
      options={PADDING_SIZES_WITH_NONE}
      value={settings.eventsItemPadding}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('eventsItemPadding', newValue as SettingsType['eventsItemPadding']);
      }}
    />
  );
}

function EventsItemGap({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Item gap"
      options={PADDING_SIZES}
      value={settings.eventsItemGap}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('eventsItemGap', newValue as SettingsType['eventsItemGap']);
      }}
    />
  );
}

function EventsItemBorderRadius({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Item radius"
      options={PADDING_SIZES_WITH_NONE}
      value={settings.eventsItemBorderRadius}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('eventsItemBorderRadius', newValue as SettingsType['eventsItemBorderRadius']);
      }}
    />
  );
}

function EventsItemImageColumn({ settings, setSetting, boardInfo, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = boardInfo.fields
    .filter((field) => field.type === 'file')
    .map((field) => ({ label: field.label, value: field.id, leftIcon: Column }));

  options.unshift({ label: 'None', value: '__none__', leftIcon: Hide });

  return (
    <SelectSetting
      title="Item image column"
      options={options}
      value={!settings.eventsItemImageColumn ? '__none__' : settings.eventsItemImageColumn}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        if (newValue === '__none__') {
          setSetting('eventsItemImageColumn', null);
        } else {
          setSetting('eventsItemImageColumn', newValue as SettingsType['eventsItemImageColumn']);
        }
      }}
    />
  );
}

function EventsItemHeadlineColumn({ settings, setSetting, boardInfo, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = boardInfo.fields
    .filter((field) => field.type === 'text' || field.type === 'name')
    .map((field) => ({ label: field.label, value: field.id, leftIcon: Column }));

  options.unshift({ label: 'None', value: '__none__', leftIcon: Hide });

  return (
    <SelectSetting
      title="Item headline column"
      options={options}
      value={!settings.eventsItemHeadlineColumn ? '__none__' : settings.eventsItemHeadlineColumn}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        if (newValue === '__none__') {
          setSetting('eventsItemHeadlineColumn', null);
        } else {
          setSetting('eventsItemHeadlineColumn', newValue as SettingsType['eventsItemHeadlineColumn']);
        }
      }}
    />
  );
}

function EventsItemDateColumn({ settings, setSetting, boardInfo, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = boardInfo.fields
    .filter((field) => field.type === 'date')
    .map((field) => ({ label: field.label, value: field.id, leftIcon: Column }));

  options.unshift({ label: 'None', value: '__none__', leftIcon: Hide });

  return (
    <SelectSetting
      title="Item date column"
      options={options}
      value={!settings.eventsItemDateColumn ? '__none__' : settings.eventsItemDateColumn}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        if (newValue === '__none__') {
          setSetting('eventsItemDateColumn', null);
        } else {
          setSetting('eventsItemDateColumn', newValue as SettingsType['eventsItemDateColumn']);
        }
      }}
    />
  );
}

function EventsDateFormat({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Date format"
      options={DATE_FORMATS}
      value={settings.eventsDateFormat}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('eventsDateFormat', newValue as SettingsType['eventsDateFormat']);
      }}
    />
  );
}

function EventsTimeFormat({ settings, setSetting, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  return (
    <SelectSetting
      title="Time format"
      options={TIME_FORMATS}
      value={settings.eventsTimeFormat}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        setSetting('eventsTimeFormat', newValue as SettingsType['eventsTimeFormat']);
      }}
    />
  );
}

function EventsItemLocationColumn({ settings, setSetting, boardInfo, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = boardInfo.fields
    .filter((field) => field.type === 'location')
    .map((field) => ({ label: field.label, value: field.id, leftIcon: Column }));

  options.unshift({ label: 'None', value: '__none__', leftIcon: Hide });

  return (
    <SelectSetting
      title="Item location column"
      options={options}
      value={!settings.eventsItemLocationColumn ? '__none__' : settings.eventsItemLocationColumn}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        if (newValue === '__none__') {
          setSetting('eventsItemLocationColumn', null);
        } else {
          setSetting('eventsItemLocationColumn', newValue as SettingsType['eventsItemLocationColumn']);
        }
      }}
    />
  );
}

function EventsItemDescriptionColumn({ settings, setSetting, boardInfo, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = boardInfo.fields
    .filter((field) => field.type === 'text' || field.type === 'long_text')
    .map((field) => ({ label: field.label, value: field.id, leftIcon: Column }));

  options.unshift({ label: 'None', value: '__none__', leftIcon: Hide });

  return (
    <SelectSetting
      title="Item description column"
      options={options}
      value={!settings.eventsItemDescriptionColumn ? '__none__' : settings.eventsItemDescriptionColumn}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        if (newValue === '__none__') {
          setSetting('eventsItemDescriptionColumn', null);
        } else {
          setSetting('eventsItemDescriptionColumn', newValue as SettingsType['eventsItemDescriptionColumn']);
        }
      }}
    />
  );
}

function EventsItemLearnMoreColumn({ settings, setSetting, boardInfo, setHelpState, settingCategory, setShowHelpModal }: SettingProps) {
  const options = boardInfo.fields
    .filter((field) => field.type === 'link')
    .map((field) => ({ label: field.label, value: field.id, leftIcon: Column }));

  options.unshift({ label: 'None', value: '__none__', leftIcon: Hide });

  return (
    <SelectSetting
      title="Item learn more column"
      options={options}
      value={!settings.eventsItemLearnMoreColumn ? '__none__' : settings.eventsItemLearnMoreColumn}
      setHelpState={setHelpState}
      settingCategory={settingCategory}
      setShowHelpModal={setShowHelpModal}
      onChange={(newValue) => {
        if (newValue === '__none__') {
          setSetting('eventsItemLearnMoreColumn', null);
        } else {
          setSetting('eventsItemLearnMoreColumn', newValue as SettingsType['eventsItemLearnMoreColumn']);
        }
      }}
    />
  );
}

const SETTING_TO_COMPONENT: Record<
  keyof Omit<
    SettingsType,
    'mode' | 'name' | 'columnSettings' | 'boardFilters' | 'customLabels' | 'enableFormulaSupport' | 'enableSubitemsSupport'
  >,
  (props: SettingProps) => JSX.Element
> = {
  headline: Headline,
  bodyPadding: BodyPadding,
  primaryColor: PrimaryColor,
  secondaryColor: SecondaryColor,
  borderColor: BorderColor,
  backgroundColor: BackgroundColor,
  fontFamily: FontFamily,
  fontSize: FontSize,
  textAlignment: TextAlignment,
  sortColumn: SortColumn,
  sortDirection: SortDirection,
  search: Search,
  tableShowHeader: TableShowHeader,
  tableCellPadding: TableCellPadding,
  tableBorderRadius: TableBorderRadius,
  tableDefaultItemsPerPage: TableDefaultItemsPerPage,
  gridItemsPerPage: GridItemsPerPage,
  gridMode: GridMode,
  gridItemPadding: GridItemPadding,
  gridItemGap: GridItemGap,
  gridItemBorderRadius: GridItemBorderRadius,
  gridItemImageColumn: GridItemImageColumn,
  gridItemImageColumnFormat: GridItemImageColumnFormat,
  gridItemImageMode: GridItemImageMode,
  gridItemHeadlineColumn: GridItemHeadlineColumn,
  eventsItemPadding: EventsItemPadding,
  eventsItemGap: EventsItemGap,
  eventsItemBorderRadius: EventsItemBorderRadius,
  eventsItemImageColumn: EventsItemImageColumn,
  eventsItemHeadlineColumn: EventsItemHeadlineColumn,
  eventsItemDateColumn: EventsItemDateColumn,
  eventsItemLocationColumn: EventsItemLocationColumn,
  eventsItemDescriptionColumn: EventsItemDescriptionColumn,
  eventsItemLearnMoreColumn: EventsItemLearnMoreColumn,
  eventsDateFormat: EventsDateFormat,
  eventsTimeFormat: EventsTimeFormat,
};

type SettingsBuilderProps = {
  settingKeys: (keyof typeof SETTING_TO_COMPONENT)[];
  settings: SettingsType;
  setSetting: <K extends keyof SettingsType>(key: K, value: SettingsType[K]) => void;
  boardInfo: ViewerBoardInfo;
  setHelpState: any;
  settingCategory: string;
  setShowHelpModal: any;
};

function SettingsBuilder({
  settingKeys,
  settings,
  setSetting,
  boardInfo,
  setHelpState,
  settingCategory,
  setShowHelpModal,
}: SettingsBuilderProps) {
  return (
    <>
      {settingKeys.map((key) => {
        if (key in SETTING_TO_COMPONENT) {
          const Component = SETTING_TO_COMPONENT[key];
          return (
            <Component
              key={key}
              settings={settings}
              setSetting={setSetting}
              boardInfo={boardInfo}
              setHelpState={setHelpState}
              settingCategory={settingCategory}
              setShowHelpModal={setShowHelpModal}
            />
          );
        }
      })}
    </>
  );
}

type GeneralSettingsProps = {
  settingKeys?: (keyof typeof SETTING_TO_COMPONENT)[];
  settings: SettingsType;
  setSetting: <K extends keyof SettingsType>(key: K, value: SettingsType[K]) => void;
  boardInfo: ViewerBoardInfo;
  setHelpState: any;
  settingCategory: string;
  setShowHelpModal: any;
};

function GeneralSettings({
  settings,
  setSetting,
  boardInfo,
  settingKeys = [
    'headline',
    'bodyPadding',
    'primaryColor',
    'secondaryColor',
    'borderColor',
    'backgroundColor',
    'fontFamily',
    'fontSize',
    'textAlignment',
    'sortColumn',
    'sortDirection',
  ],
  setHelpState,
  setShowHelpModal,
}: GeneralSettingsProps) {
  return (
    <SidebarWidget
      collapsed
      title="General settings"
      description="Define the outer margin, choose colors and adapt font settings on your widget."
      icon={Settings}
    >
      <SettingsBuilder
        settingKeys={settingKeys}
        settings={settings}
        setSetting={setSetting}
        boardInfo={boardInfo}
        setHelpState={setHelpState}
        settingCategory="general_settings"
        setShowHelpModal={setShowHelpModal}
      />
    </SidebarWidget>
  );
}

export function ColumnSettings({ settings, setSetting, boardInfo }: GeneralSettingsProps) {
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const selectedField = selectedFieldId ? boardInfo.fields.find((field) => field.id === selectedFieldId) : null;

  return (
    <>
      {selectedField ? (
        <ColumnSettingsModal
          show
          field={selectedField}
          columnSettings={settings?.columnSettings?.[selectedField.id] || {}}
          onClose={(newColumnSettings) => {
            if (newColumnSettings) {
              const columnSettings = settings?.columnSettings || {};

              setSetting('columnSettings', {
                ...columnSettings,
                [selectedField.id]: newColumnSettings,
              });
            }
            setSelectedFieldId(null);
          }}
        />
      ) : null}

      <SidebarWidget
        collapsed
        title="Column settings"
        description="Hide certain board columns in your widget and apply advanced settings."
        icon={Column}
      >
        <Flex style={{ marginBottom: '8px', width: '100%' }}>
          <Button
            size="small"
            kind="secondary"
            rightFlat
            preventClickAnimation
            onClick={() => {
              const columnSettings = settings?.columnSettings || {};
              const newColumnSettings = { ...columnSettings };
              boardInfo.fields.forEach((f) => {
                newColumnSettings[f.id] = {
                  ...(f.id in columnSettings ? columnSettings[f.id] : {}),
                  hidden: false,
                };
              });
              setSetting('columnSettings', newColumnSettings);
            }}
            style={{
              marginRight: '-1px',
              width: '50%',
            }}
          >
            Show all
          </Button>
          <Button
            size="small"
            kind="secondary"
            leftFlat
            preventClickAnimation
            onClick={() => {
              const columnSettings = settings?.columnSettings || {};
              const newColumnSettings = { ...columnSettings };
              boardInfo.fields.forEach((f) => {
                newColumnSettings[f.id] = {
                  ...(f.id in columnSettings ? columnSettings[f.id] : {}),
                  hidden: true,
                };
              });
              setSetting('columnSettings', newColumnSettings);
            }}
            style={{
              width: '50%',
            }}
          >
            Hide all
          </Button>
        </Flex>

        {boardInfo.fields.map((field) => {
          const isHidden = _.get(settings, ['columnSettings', field.id, 'hidden']) ? true : false;

          {
            /* REFACTOR hasSettings CHECK IF NEEDED */
          }

          const hasSettings = (() => {
            const col = settings?.columnSettings?.[field.id];
            if (!col) return false;

            return (
              col.showLabel === true ||
              col.labelMode === 'inline' ||
              col.filesDisplayMode === 'thumbs' ||
              col.summarizeNumbers === true ||
              col.enforceLineBreaks === true ||
              col.excludeFromSearch === true ||
              col.showAsFilter === true
            );
          })();

          return (
            <>
              {!settings.enableFormulaSupport && field.type === 'formula' ? (
                <Tooltip content="Enable formula columns first within the advanced settings" position="right">
                  <Flex key={field.id} gap={10}>
                    <IconButton
                      icon={isHidden ? Hide : Show}
                      kind={isHidden ? 'secondary' : 'primary'}
                      size="xs"
                      disabled
                      onClick={() => {
                        const columnSettings = settings?.columnSettings || {};

                        setSetting('columnSettings', {
                          ...columnSettings,
                          [field.id]: {
                            ...(field.id in columnSettings ? columnSettings[field.id] : {}),
                            hidden: !isHidden,
                          },
                        });
                      }}
                    />
                    <div
                      style={{
                        overflow: 'hidden',
                        width: '184px',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        color: '#32333861',
                      }}
                    >
                      {field.label}
                    </div>
                    <IconButton
                      icon={Settings}
                      kind={hasSettings ? 'secondary' : 'tertiary'}
                      size="xs"
                      disabled
                      onClick={() => {
                        setSelectedFieldId(field.id);
                      }}
                    />
                  </Flex>
                </Tooltip>
              ) : (
                <Flex key={field.id} gap={10}>
                  <IconButton
                    icon={isHidden ? Hide : Show}
                    kind={isHidden ? 'secondary' : 'primary'}
                    size="xs"
                    onClick={() => {
                      const columnSettings = settings?.columnSettings || {};

                      setSetting('columnSettings', {
                        ...columnSettings,
                        [field.id]: {
                          ...(field.id in columnSettings ? columnSettings[field.id] : {}),
                          hidden: !isHidden,
                        },
                      });
                    }}
                  />
                  <div
                    style={{
                      overflow: 'hidden',
                      width: '184px',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {field.label}
                  </div>
                  <IconButton
                    icon={Settings}
                    kind={hasSettings ? 'secondary' : 'tertiary'}
                    size="xs"
                    onClick={() => {
                      setSelectedFieldId(field.id);
                    }}
                  />
                </Flex>
              )}
            </>
          );
        })}
      </SidebarWidget>
    </>
  );
}

type ModeSettingsProps = {
  settings: SettingsType;
  setSetting: <K extends keyof SettingsType>(key: K, value: SettingsType[K]) => void;
  boardInfo: ViewerBoardInfo;
  setHelpState: any;
  settingCategory: string;
  setShowHelpModal: any;
};

function TableModeSettings({ settings, setSetting, boardInfo, setHelpState, setShowHelpModal }: ModeSettingsProps) {
  return (
    <>
      <SidebarWidget
        collapsed
        title="Table settings"
        description="Define table layout specific settings and show or hide control elements."
        icon={Table}
      >
        <SettingsBuilder
          settingKeys={['search', 'tableShowHeader', 'tableDefaultItemsPerPage', 'tableCellPadding', 'tableBorderRadius']}
          settings={settings}
          setSetting={setSetting}
          boardInfo={boardInfo}
          setHelpState={setHelpState}
          settingCategory="table_settings"
          setShowHelpModal={setShowHelpModal}
        />
      </SidebarWidget>
      <GeneralSettings
        settings={settings}
        setSetting={setSetting}
        boardInfo={boardInfo}
        setHelpState={setHelpState}
        settingCategory="general_settings"
        setShowHelpModal={setShowHelpModal}
      />
    </>
  );
}

function GridModeSettings({ settings, setSetting, boardInfo, setHelpState, setShowHelpModal }: ModeSettingsProps) {
  return (
    <>
      <SidebarWidget
        collapsed
        title="Grid settings"
        description="Define grid layout specific settings, choose the main image and headline."
        icon={Workspace}
      >
        <SettingsBuilder
          settingKeys={[
            'search',
            'gridMode',
            'gridItemsPerPage',
            'gridItemPadding',
            'gridItemGap',
            'gridItemBorderRadius',
            'gridItemImageColumn',
            'gridItemImageColumnFormat',
            'gridItemImageMode',
            'gridItemHeadlineColumn',
          ]}
          settings={settings}
          setSetting={setSetting}
          boardInfo={boardInfo}
          setHelpState={setHelpState}
          settingCategory="grid_settings"
          setShowHelpModal={setShowHelpModal}
        />
      </SidebarWidget>
      <GeneralSettings
        settings={settings}
        setSetting={setSetting}
        boardInfo={boardInfo}
        setHelpState={setHelpState}
        settingCategory="grid_settings"
        setShowHelpModal={setShowHelpModal}
      />
    </>
  );
}

function EventsModeSettings({ settings, setSetting, boardInfo, setHelpState, setShowHelpModal }: ModeSettingsProps) {
  return (
    <>
      <SidebarWidget
        collapsed
        title="Events settings"
        description="Define events layout specific settings, choose the main image and headline."
        icon={Status}
      >
        <SettingsBuilder
          settingKeys={[
            'search',
            'eventsItemPadding',
            'eventsItemGap',
            'eventsItemBorderRadius',
            'eventsItemImageColumn',
            'eventsItemHeadlineColumn',
            'eventsItemDateColumn',
            'eventsDateFormat',
            'eventsTimeFormat',
            'eventsItemLocationColumn',
            'eventsItemDescriptionColumn',
            'eventsItemLearnMoreColumn',
          ]}
          settings={settings}
          setSetting={setSetting}
          boardInfo={boardInfo}
          setHelpState={setHelpState}
          settingCategory="events_settings"
          setShowHelpModal={setShowHelpModal}
        />
      </SidebarWidget>
      <GeneralSettings
        settings={settings}
        setSetting={setSetting}
        boardInfo={boardInfo}
        setHelpState={setHelpState}
        settingCategory="events_settings"
        settingKeys={[
          'headline',
          'bodyPadding',
          'primaryColor',
          'secondaryColor',
          'borderColor',
          'backgroundColor',
          'fontFamily',
          'fontSize',
        ]}
        setShowHelpModal={setShowHelpModal}
      />
    </>
  );
}

export const MODE_TO_SETTINGS: Record<SettingsType['mode'], (props: ModeSettingsProps) => JSX.Element> = {
  table: TableModeSettings,
  grid: GridModeSettings,
  events: EventsModeSettings,
};

export const MODES: { label: string; value: SettingsType['mode']; icon: any; description: string }[] = [
  {
    label: 'Table',
    value: 'table',
    icon: Table,
    description: 'This layout mode lets you display board items in a sortable table, optionally with subitems.',
  },
  {
    label: 'Grid',
    value: 'grid',
    icon: Workspace,
    description: 'This layout mode turns board items into a grid, optionally with thumbnail images.',
  },
  {
    label: 'Events',
    value: 'events',
    icon: Status,
    description: 'This layout mode is dedicated to displaying upcoming and past events in chronological order.',
  },
];

export function LayoutsSettings({ settings, setSetting }: SettingProps) {
  return (
    <SidebarWidget title="Layout" description="Select a widget layout that you want to use to display your board items." icon={Layout}>
      <div className="WidgetTypeWrapper">
        {MODES.map((mode) => (
          <Tooltip
            key={mode.value}
            title={`Layout → ${mode.label}`}
            content={mode.description}
            position="right"
            showDelay={100}
            hideDelay={0}
            showOnDialogEnter={false}
            data-testid="tooltip"
          >
            <div
              className={settings.mode === mode.value ? 'WidgetType WidgetTypeActive' : 'WidgetType'}
              onClick={() => {
                setSetting('mode', mode.value);
              }}
            >
              <div className="WidgetTypeInner">
                <Icon icon={mode.icon} size={mode.value === 'events' ? 19 : 20} />
                <h5>{mode.label}</h5>
              </div>
            </div>
          </Tooltip>
        ))}
      </div>
    </SidebarWidget>
  );
}
